import React, { useEffect, useRef, useCallback } from 'react';
import { ContentCard as ContentCardType, PersonSummaryWithPhoto } from '../types';
import ContentCard from './ContentCard';

interface ContentGridProps {
  content: ContentCardType[];
  onContentClick?: (content: ContentCardType) => void;
  onPersonClick?: (person: PersonSummaryWithPhoto) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  className?: string;
  showPopularityScores?: boolean;
  isFiltered?: boolean; // Indicates if content is filtered (e.g., top 500 popular)
  emptyStateMessage?: string;
}

const ContentGrid: React.FC<ContentGridProps> = ({
  content,
  onContentClick,
  onPersonClick,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  className = '',
  showPopularityScores = false,
  isFiltered = false,
  emptyStateMessage,
}) => {
  const observerRef = useRef<IntersectionObserver>();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const lastContentElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();
      
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && onLoadMore) {
          onLoadMore();
        }
      });
      
      if (node) observerRef.current.observe(node);
    },
    [isLoading, hasMore, onLoadMore]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  if (!content || content.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 text-lg mb-4">
          {emptyStateMessage || (isFiltered ? 'No popular content available' : 'No content found')}
        </div>
        <p className="text-gray-500">
          {isFiltered 
            ? 'Content is filtered to show only the most popular titles'
            : 'Try adjusting your filters or search terms'
          }
        </p>
        {isFiltered && (
          <div className="flex items-center justify-center mt-4 text-orange-400">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
            </svg>
            Showing Popular Content Only
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Filter status indicator */}
      {isFiltered && content.length > 0 && (
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-800/50 rounded-lg border border-orange-500/20">
          <div className="flex items-center text-orange-400">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Showing Popular Content</span>
          </div>
          <div className="text-sm text-gray-400">
            {content.length} titles from top 500 most popular
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
        {content.map((item, index) => (
          <div
            key={item.id}
            ref={index === content.length - 1 ? lastContentElementRef : undefined}
            className="relative"
          >
            <ContentCard
              content={item}
              onClick={onContentClick}
              onPersonClick={onPersonClick}
              showPopularityScore={showPopularityScores}
            />
            {/* Popular content indicator for filtered grids */}
            {isFiltered && (
              <div className="absolute top-2 right-2 bg-orange-500/90 text-white text-xs font-medium px-2 py-1 rounded-full shadow-lg z-20">
                <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
                Popular
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6 mt-6">
          {[...Array(12)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-800 rounded-lg h-64 sm:h-72 md:h-80"></div>
            </div>
          ))}
        </div>
      )}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="h-10" />
    </div>
  );
};

export default ContentGrid;